import { useEffect, useState, type JSX } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { rotuloDeNivel } from '@orc/shared';
import { requisitar } from '../lib/api.js';
import { rotuloTabela } from '../lib/formato.js';
import './BuscaProduto.css';

interface ProdutoBusca {
  id: string;
  referencia: string;
  formato: string | null;
  unidade: string;
  classeCode: string;
  atributosJson: Record<string, unknown> | null;
  /**
   * Há preço na tabela pedida? Só vem quando a busca informa `tabela`; ausente
   * significa "não perguntei", que é diferente de "não tem".
   */
  temPrecoNaTabela?: boolean;
}

/**
 * Busca de produto para adicionar ao orçamento — Story 8.3.
 *
 * Consome a **mesma** rota do catálogo (Story 8.2), então herda de graça a
 * ordenação por tamanho e a busca por referência, formato e linha. Só produtos
 * ativos: orçar item inativo é erro que só aparece na hora de faturar.
 */
export function BuscaProduto({
  aoEscolher,
  desabilitado,
  tabela,
  delNivel,
  classe,
}: {
  aoEscolher: (produto: ProdutoBusca) => void;
  desabilitado?: boolean;
  /** Tabela do orçamento em edição: marca quem não tem preço nela. */
  tabela?: string;
  /**
   * Nível del credere do orçamento — Épico 9.
   *
   * No del credere a marcação de preço depende da COLUNA, não da classe do
   * produto: sem ele o servidor devolveria `temPrecoNaTabela: false` para
   * todos, e a lista apareceria inteira desabilitada.
   */
  delNivel?: string;
  /**
   * Classe da SEÇÃO — S5, as telas "Vinílicos" e "Rodapé Engenharia".
   *
   * A busca passa a oferecer só a classe da seção: é o que faz a tela ser
   * daquela família. Não é trava — o del credere não trava classe, e estas
   * espelham isso. É a porta, e um item de outra classe que chegue por outro
   * caminho continua valendo.
   */
  classe?: string;
}): JSX.Element {
  const [termo, setTermo] = useState('');
  const [atrasado, setAtrasado] = useState('');
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setAtrasado(termo);
    }, 250);
    return () => {
      clearTimeout(t);
    };
  }, [termo]);

  const produtos = useQuery({
    queryKey: ['busca-produto', atrasado, tabela, delNivel, classe],
    queryFn: async () =>
      (
        await requisitar<ProdutoBusca[]>(
          `/api/produtos?apenasAtivos=true&limite=12&busca=${encodeURIComponent(atrasado)}` +
            (tabela === undefined ? '' : `&tabela=${encodeURIComponent(tabela)}`) +
            (delNivel === undefined ? '' : `&delNivel=${encodeURIComponent(delNivel)}`) +
            (classe === undefined ? '' : `&classe=${encodeURIComponent(classe)}`),
        )
      ).dados,
    enabled: atrasado.trim().length >= 2,
    placeholderData: keepPreviousData,
  });

  const resultados = atrasado.trim().length >= 2 ? (produtos.data ?? []) : [];

  return (
    /*
     * O fechamento do dropdown é do CONTÊINER, não do input.
     *
     * Antes, o `onBlur` do input agendava `setAberto(false)` em 150ms. Um
     * clique humano dura 100–300ms: nesse intervalo a lista desmontava, e o
     * navegador **não dispara `click` quando o alvo do `mousedown` sai do
     * DOM**. O resultado era o pior tipo de falha — o dropdown aparecia, o
     * clique não fazia nada, e não havia erro no console nem requisição na
     * aba Network.
     *
     * `relatedTarget` é para onde o foco está indo: se for para dentro deste
     * componente (um resultado alcançado por Tab, por exemplo), a lista
     * continua aberta. Só fecha quando o foco sai de tudo.
     */
    <div
      className="busca-produto"
      onBlur={(e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setAberto(false);
      }}
    >
      <label className="campo">
        <span className="apenas-leitor">Buscar produto para adicionar</span>
        <input
          className="campo__entrada"
          type="search"
          value={termo}
          disabled={desabilitado ?? false}
          placeholder="Adicionar item — busque por referência, formato ou linha…"
          onChange={(e) => {
            setTermo(e.target.value);
            setAberto(true);
          }}
          onFocus={() => {
            setAberto(true);
          }}
        />
      </label>

      {aberto && termo.trim().length >= 2 && (
        <ul
          className="busca-produto__lista"
          role="listbox"
          /*
           * `preventDefault` no `mousedown` impede o input de perder o foco.
           * Sem blur, não há fechamento — e o `click` chega ao botão com a
           * lista ainda montada. Vale para a área toda da lista, inclusive a
           * barra de rolagem, que também roubaria o foco.
           */
          onMouseDown={(e) => {
            e.preventDefault();
          }}
        >
          {produtos.isFetching && resultados.length === 0 ? (
            <li className="busca-produto__vazio">Buscando…</li>
          ) : resultados.length === 0 ? (
            <li className="busca-produto__vazio">Nenhum produto ativo encontrado.</li>
          ) : (
            resultados.map((p) => {
              /*
               * A fábrica zera a coluna de preço para dizer "não vendemos nesta
               * tabela". O produto **aparece**, marcado e desabilitado — sumir
               * com ele faria o vendedor concluir que a referência não existe e
               * ir procurá-la no catálogo. Ver o produto e a razão é o que ele
               * precisa para trocar a tabela do orçamento.
               */
              const semPreco = p.temPrecoNaTabela === false;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    className={
                      semPreco
                        ? 'busca-produto__item busca-produto__item--sem-preco'
                        : 'busca-produto__item'
                    }
                    disabled={semPreco}
                    /*
                      O rótulo, não a chave crua.

                      Isto dizia "Sem preço na coluna del_5" — e `del_5` não
                      diz nada a quem vende. Com as comissões da S1 seria pior
                      ainda: "com_4_0". `rotuloDeNivel` é a mesma fonte única
                      que a trilha e o seletor usam.
                    */
                    title={
                      semPreco
                        ? delNivel === undefined
                          ? `Sem preço na tabela ${tabela === undefined ? '' : rotuloTabela(tabela)} deste orçamento.`
                          : `Sem preço na coluna ${rotuloDeNivel(delNivel) ?? delNivel} deste orçamento.`
                        : undefined
                    }
                    onClick={() => {
                      aoEscolher(p);
                      setTermo('');
                      setAtrasado('');
                      setAberto(false);
                    }}
                  >
                    <span className="busca-produto__ref">{p.referencia}</span>
                    <span className="busca-produto__desc">
                      {p.formato ?? '—'}
                      {typeof p.atributosJson?.['linha'] === 'string'
                        ? ` · ${String(p.atributosJson['linha'])}`
                        : ''}
                    </span>
                    {semPreco ? (
                      <span className="busca-produto__tag">
                        sem preço em{' '}
                        {delNivel === undefined
                          ? tabela === undefined
                            ? 'nesta tabela'
                            : rotuloTabela(tabela)
                          : (rotuloDeNivel(delNivel) ?? delNivel)}
                      </span>
                    ) : (
                      <span className="busca-produto__unidade">
                        {p.unidade === 'metro_linear' ? 'm linear' : 'm²'}
                      </span>
                    )}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}

export type { ProdutoBusca };
